<template>
  <div class="wrapper">
    <div class="campaign-side-column p-col-fixed">
      <SideColumn :steps="steps" @save="showSaveAndExitModal"/>
    </div>
    <div class="campaign-content p-col">

      <template v-for="(step, index) in steps" :key="step.id">
        <template v-if="step.id === 'settings' && step.active">
          <SuspenseIndicator class="p-d-flex p-justify-center">
            <CampaignForm v-model:campaign="campaign" @onNext="onNextStep(index)" :price="campaignPrice" :brand="brand"/>
          </SuspenseIndicator>
        </template>

        <template v-if="step.id === 'templates' && step.active">
          <SuspenseIndicator class="p-d-flex p-justify-center">
            <Templates v-model:campaign="campaign" @onBack="onBackStep(index)" @onNext="onNextStep(index)"/>
          </SuspenseIndicator>
        </template>

        <template v-if="step.id === 'scripts' && step.active">
          <SuspenseIndicator class="p-d-flex p-justify-center">
            <Scripts v-model:campaign="campaign" @onBack="onBackStep(index)" @onNext="onNextStep(index)"/>
          </SuspenseIndicator>
        </template>

        <template v-if="step.id === 'checkout' && step.active">
          <SuspenseIndicator class="p-d-flex p-justify-center">
            <Checkout v-model:campaign="campaign" @onBack="onBackStep(index)" :price="campaignPrice"/>
          </SuspenseIndicator>
        </template>
      </template>

    </div>
  </div>

  <SaveAndExitModal v-model:visible="saveAndExitModalVisible" @save="onSaveAndExit" @exit="onExit"/>
</template>

<script setup>
import {computed, ref} from "vue";
import SideColumn from "./campaign-add/SideColumn";
import CampaignForm from "./campaign-add/CampaignForm";
import Templates from "./campaign-add/Templates";
import Scripts from "./campaign-add/Scripts";
import Checkout from "./campaign-add/Checkout";
import SaveAndExitModal from "./campaign-add/SaveAndExitModal";
import {useCampaign} from "../../hooks/useCampaign";
import {useRoute, useRouter} from "vue-router";
import SuspenseIndicator from "../SuspenseIndicator";
import {useDemographic} from "../../hooks/useDemographic";
import {currentBrand} from "../../queries/brandQueries";

const route = useRoute();
const router = useRouter();
const {draft, prepareToSave, getCampaign, update, create, isStatusActive} = useCampaign();
const {isGender, isAge} = useDemographic();

const steps = ref([
  {id: 'settings', title: 'Campaign Settings', done: false, active: true},
  {id: 'templates', title: 'Campaign Script Template', done: false, active: false},
  {id: 'scripts', title: 'Customize Script', done: false, active: false},
  {id: 'checkout', title: 'Confirmation', done: false, active: false},
]);
const id = ref(route.params.id);
const {data} = currentBrand();
const brand = computed(() => data.value?.data || {});
const campaignPrice = computed(() => brand.value.campaign_price ? brand.value.campaign_price / 100 : 0)

const defaultCampaignData = {
  id: null,
  title: '',
  products: [],
  requirements: [],
  questions: [],
  scripts: [],
  age: [],
  gender: [],
  ad_format_options: [],
  number_of_creators: null,
  ad_template_id: null,
  is_custom_template: false,
};

const prepareBackEndData = (data) => {
  data.age = data.demographics.filter(({ category}) => isAge(category) );
  data.gender = data.demographics.filter(({ category}) => isGender(category));
  data.ad_format_options = data.ad_format_options.map(({ id }) => id);

  if (data.scripts?.length && data.scripts[0].template) {
    data.ad_template_id = data.scripts[0].template.id
  }

  if (data.scripts?.length) {
    data.is_custom_template = data.scripts.filter(script => !script.template).length > 0;
  }

  delete data.demographics;

  return data;
}

const campaign = ref(id.value ? prepareBackEndData((await getCampaign(id.value)).data) : {...defaultCampaignData});
const saveAndExitModalVisible = ref(false);

const redirectToCampaigns = () => router.push({'name': 'campaigns.list'});

if (isStatusActive(campaign.value.status)) {
  redirectToCampaigns();
}

const onBackStep = (index) => {
  steps.value[index].done = false;
  steps.value[index].active = false;
  steps.value[index - 1].active = true;
}

const onNextStep = (index) => {
  steps.value[index].done = true;
  steps.value[index].active = false;
  steps.value[index + 1].active = true;
}

const showSaveAndExitModal = () => saveAndExitModalVisible.value = true;
const onSaveAndExit = async () => {
  const data = prepareToSave(campaign.value);

  await (id.value ? update(id.value, data): draft(data));
  await redirectToCampaigns();
}
const onExit = () => redirectToCampaigns()

</script>

<style lang="scss" scoped>
@import "../../assets/sass/theme_variables";

.wrapper {
  display: flex;
  min-height: 100vh;
  background: $bg;
}

.campaign-side-column {
  display: flex;
  flex-direction: column;
  padding: toRem(16) toRem(16) toRem(24) toRem(32);
  width: toRem(300);
  height: 100vh;
  overflow-y: auto;
}

.campaign-create-footer {
  .next-button {
    font-weight: 500;
    padding: toRem(10) toRem(60);
  }
}

.campaign-create-footer-content {
  width: 100%;
  display: flex;
  max-width: toRem(924);
  margin: 0 auto;
}

.campaign-content {
  display: flex;
  height: 100vh;
  min-height: 100%;
  flex-direction: column;
  padding: toRem(40) toRem(24) 0;
  background: $bgBlocks;
  box-shadow: 6px 0 43px -4px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
}

</style>
